// ------------------------------------------------------------------------------
// <copyright file="manualQaApiService.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

import { getUser } from '../utils/auth';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getUserFromToken } from '../utils/jwtService';

const DEFAULT_CATALOG_ID = 'iosclient-view-plan-v1';
const MANUAL_QA_MASTER_TENANT_ID = 'c2hl';

const getBaseUrl = () => {
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const baseUrl = process.env.REACT_APP_FUNCTIONS_URL || (isLocalDev ? 'http://localhost:7071/api' : 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api');
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const statusMap = {
  'Not run': 'notRun',
  Pass: 'pass',
  Bug: 'bug',
  Blocked: 'blocked',
  'N/A': 'notApplicable',
};

const severityMap = {
  Blocker: 'blocker',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

/**
 * Returns tester identity for Manual QA API payloads.
 *
 * @returns {{testerUserId: string, tenantId: string, role: string, email: string}}
 */
function getManualQaUserContext(runState = {}) {
  const tokenUser = getUserFromToken();
  const storedUser = getUser();

  return {
    testerUserId: tokenUser?.userId || runState.testerUserId || storedUser?.RowKey || storedUser?.rowKey || 'manual-qa-local-tester',
    tenantId: tokenUser?.tenantId || runState.tenantId || storedUser?.TenantId || storedUser?.tenantId || 'manual-qa-local-tenant',
    role: tokenUser?.role || storedUser?.Role || storedUser?.role || 'Tester',
    email: tokenUser?.email || storedUser?.Email || storedUser?.email || '',
  };
}

function getRunId(runState) {
  return runState?.runId || `manual-qa-${runState?.startedAt || Date.now()}`;
}

function getRunDocumentId(runState) {
  return `run_${getRunId(runState)}`;
}

/**
 * Canonical Manual QA assignment ID.
 *
 * The server authorizes every tester write (autosave, bug, completion) against an
 * assignment document it can READ -- see AuthorizeActiveAssignmentAsync. So the ID the
 * runner sends must be byte-identical to the one Campaign Builder wrote. It was not:
 * the runner derived it from a browser-generated runId (`assignment-manual-qa-<epoch>-<catalog>`)
 * while the admin wrote `assignment-<testerUserId>-<catalogId>`, which can never match.
 * Every tester write therefore 403'd with "Active Manual QA assignment required" and the
 * runner fell back to localStorage, so nothing reached the admin workspace.
 *
 * Both sides now call this one function. Do not reintroduce a runId-derived form: runId is
 * minted per browser, so it is not knowable by the admin who creates the assignment.
 *
 * @param {string} testerUserId - Tester user identifier (JWT subject).
 * @param {string} catalogId - Catalog the tester is assigned.
 * @returns {string} Assignment identifier shared by runner and Campaign Builder.
 */
function buildManualQaAssignmentId(testerUserId, catalogId) {
  return `assignment-${testerUserId}-${catalogId}`;
}

function getAssignmentId(runState) {
  const user = getManualQaUserContext(runState);
  return buildManualQaAssignmentId(user.testerUserId, getCatalogId(runState));
}

function getCatalogId(runState) {
  return runState?.catalogId || DEFAULT_CATALOG_ID;
}

function isoFromMilliseconds(value) {
  return new Date(value || Date.now()).toISOString();
}

function getDurationSeconds(runState) {
  const startedAt = Number(runState?.startedAt || Date.now());
  const updatedAt = Number(runState?.updatedAt || Date.now());
  return Math.max(0, Math.round((updatedAt - startedAt) / 1000));
}

function getStatus(result) {
  return statusMap[result?.status || 'Not run'] || 'notRun';
}

function getBugCount(runState, plan) {
  const activeViewIds = new Set((plan?.views || []).map(view => view.id));

  return Object.entries(runState?.viewResults || {})
    .filter(([viewId]) => activeViewIds.size === 0 || activeViewIds.has(viewId))
    .map(([, result]) => result)
    .filter(result => result.status === 'Bug')
    .length;
}

function buildStepResults(runState, plan) {
  return (plan?.views || []).flatMap((view) => {
    const result = runState?.viewResults?.[view.id] || {};
    const status = getStatus(result);

    return (view.howToTest || []).map((_, index) => ({
      scenarioId: view.parentScenarioId || view.id,
      stepId: `${view.id}-step-${index + 1}`,
      status,
      notes: result.notes || '',
      bugReportId: result.bugReportId || '',
      // Stamp only steps the tester actually ticked. The previous condition was
      // `checkedSteps[index] || status !== 'notRun' ? ... : ''`, and because ||
      // binds tighter than ?: that marked EVERY step of any card carrying a
      // status as checked -- real tick data was indistinguishable from "this
      // card has a verdict".
      checkedAtUtc: result.checkedSteps?.[index]
        ? isoFromMilliseconds(runState?.updatedAt)
        : '',
    }));
  });
}

function buildScenarioSummaries(runState, plan) {
  const summaries = new Map();

  buildStepResults(runState, plan).forEach((step) => {
    if (!summaries.has(step.scenarioId)) {
      summaries.set(step.scenarioId, {
        scenarioId: step.scenarioId,
        passedSteps: 0,
        bugSteps: 0,
        blockedSteps: 0,
        notRunSteps: 0,
      });
    }

    const summary = summaries.get(step.scenarioId);
    if (step.status === 'pass') summary.passedSteps += 1;
    else if (step.status === 'bug') summary.bugSteps += 1;
    else if (step.status === 'blocked') summary.blockedSteps += 1;
    else summary.notRunSteps += 1;
  });

  return Array.from(summaries.values());
}

function buildRunDocument(runState, plan) {
  const user = getManualQaUserContext(runState);
  const now = isoFromMilliseconds(runState?.updatedAt);

  return {
    id: getRunDocumentId(runState),
    tenantId: user.tenantId,
    testerUserId: user.testerUserId,
    assignmentId: getAssignmentId(runState),
    catalogId: getCatalogId(runState),
    status: 'inProgress',
    startedAtUtc: isoFromMilliseconds(runState?.startedAt),
    lastSavedAtUtc: now,
    systemDurationSeconds: getDurationSeconds(runState),
    testerReportedHours: Number(runState?.completion?.testerReportedHours || 0),
    bugCount: getBugCount(runState, plan),
    stepResults: buildStepResults(runState, plan),
  };
}

function buildBugDocument(runState, view, result) {
  const user = getManualQaUserContext(runState);
  const bug = result?.bug || {};

  // Leave the ID blank for a first submission so the server mints a unique one
  // (ManualQaSaveBug -> ManualQaIds.BugReport(guid)). It used to be the deterministic
  // `bug_<runId>_<viewId>`, which is the same value for every bug found on the same card:
  // a tester reporting a second finding there upserted over the first and it vanished from
  // Test Admin. Resubmits still carry the stored bugReportId, so editing a bug updates it.
  const bugId = result?.bugReportId || '';

  return {
    id: bugId,
    tenantId: user.tenantId,
    runId: getRunDocumentId(runState),
    testerUserId: user.testerUserId,
    testerGithubUsername: bug.testerGithubUsername || '',
    scenarioId: view.parentScenarioId || view.id,
    stepId: `${view.id}-step-1`,
    severity: severityMap[bug.severity || 'High'] || 'high',
    summary: bug.summary || '',
    description: bug.description || bug.summary || '',
    expectedResult: bug.expected || '',
    actualResult: bug.actual || '',
    reproSteps: bug.reproSteps || '',
    status: 'submitted',
    evidenceIds: bug.evidenceId ? [bug.evidenceId] : [],
    githubIssueState: 'pending',
  };
}

function normalizeGithubUsername(value) {
  return (value || '').trim().replace(/^@+/, '');
}

function buildTesterProfileDocument(profile) {
  return {
    tenantId: profile.tenantId || MANUAL_QA_MASTER_TENANT_ID,
    testerUserId: profile.testerUserId,
    displayName: profile.displayName || '',
    email: profile.email || '',
    githubUsername: normalizeGithubUsername(profile.githubUsername),
    status: profile.status || 'active',
    requiresPasswordChange: profile.requiresPasswordChange !== false,
    roleModel: profile.roleModel || 'manualQaProfileOnly',
  };
}

function buildTesterProvisioningRequest(profile) {
  return {
    email: profile.email || '',
    displayName: profile.displayName || '',
    githubUsername: normalizeGithubUsername(profile.githubUsername),
    hourlyRate: Number(profile.hourlyRate || 0),
  };
}

function buildScenarioCatalogDocument(catalog) {
  return {
    tenantId: catalog.tenantId || '_global',
    catalogId: catalog.catalogId || DEFAULT_CATALOG_ID,
    title: catalog.title || 'Manual QA Scenario Catalog',
    version: catalog.version || 'v1',
    surface: catalog.surface || 'iOSClient',
    groups: catalog.groups || [],
  };
}

function buildAssignmentDocument(assignment) {
  const planTier = assignment.planTier || 'Free';

  return {
    tenantId: assignment.tenantId || MANUAL_QA_MASTER_TENANT_ID,
    assignmentId: assignment.assignmentId,
    catalogId: assignment.catalogId || DEFAULT_CATALOG_ID,
    testerUserId: assignment.testerUserId,
    scenarioIds: assignment.scenarioIds || [],
    status: assignment.status || 'notStarted',
    dueAtUtc: assignment.dueAtUtc || '',
    planTier,
    targetPlanId: assignment.targetPlanId || '',
    metadata: {
      ...(assignment.metadata || {}),
      planTier,
    },
  };
}

function buildEvidenceDocument(runState, evidence) {
  const user = getManualQaUserContext(runState);

  return {
    id: evidence.id || '',
    tenantId: evidence.tenantId || user.tenantId,
    runId: evidence.runId || getRunDocumentId(runState),
    testerUserId: evidence.testerUserId || user.testerUserId,
    bugReportId: evidence.bugReportId || '',
    fileName: evidence.fileName || '',
    contentType: evidence.contentType || 'application/octet-stream',
    sizeBytes: Number(evidence.sizeBytes || 0),
    blobPath: evidence.blobPath || '',
    uploadedAtUtc: evidence.uploadedAtUtc || new Date().toISOString(),
  };
}

function buildCompletionReport(runState, plan) {
  const user = getManualQaUserContext(runState);

  return {
    tenantId: user.tenantId,
    runId: getRunId(runState),
    testerUserId: user.testerUserId,
    systemDurationSeconds: getDurationSeconds(runState),
    testerReportedHours: Number(runState?.completion?.testerReportedHours || 0),
    bugCount: getBugCount(runState, plan),
    submittedAtUtc: new Date().toISOString(),
    scenarioSummaries: buildScenarioSummaries(runState, plan),
  };
}

function buildHoursReviewDocument(runId, review) {
  return {
    tenantId: review.tenantId || MANUAL_QA_MASTER_TENANT_ID,
    runId,
    testerUserId: review.testerUserId,
    systemHours: Number(review.systemHours || 0),
    testerReportedHours: Number(review.testerReportedHours || 0),
    approvedHours: Number(review.approvedHours || 0),
    status: review.status || 'pending',
    reviewedByUserId: review.reviewedByUserId || '',
    reviewedAtUtc: review.reviewedAtUtc || new Date().toISOString(),
    adminNotes: review.adminNotes || review.reviewerNote || '',
  };
}

async function postJson(path, payload) {
  const response = await fetchWithAuth(`${getBaseUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `Manual QA request failed (${response.status})`);
  }

  return response.json();
}

async function postFormData(path, formData) {
  const response = await fetchWithAuth(`${getBaseUrl()}${path}`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `Manual QA request failed (${response.status})`);
  }

  return response.json();
}

async function getJson(path) {
  const response = await fetchWithAuth(`${getBaseUrl()}${path}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `Manual QA request failed (${response.status})`);
  }

  return response.json();
}

/**
 * Autosaves a Manual QA run draft to Functions/Cosmos.
 *
 * @param {object} runState - Local run state.
 * @param {object} plan - Scenario catalog plan.
 * @returns {Promise<object>} Persisted run document.
 */
async function autosaveRun(runState, plan) {
  return postJson('/manual-qa/runs/autosave', buildRunDocument(runState, plan));
}

/**
 * Submits a Manual QA bug report.
 *
 * @param {object} runState - Local run state.
 * @param {object} view - View plan entry.
 * @param {object} result - View result containing bug fields.
 * @returns {Promise<object>} Persisted bug report document.
 */
async function submitBug(runState, view, result) {
  return postJson('/manual-qa/bugs', buildBugDocument(runState, view, result));
}

/**
 * Saves a Manual QA tester profile.
 *
 * @param {object} profile - C2HL-owned tester profile fields.
 * @returns {Promise<object>} Persisted tester profile.
 */
async function saveTesterProfile(profile) {
  return postJson('/manual-qa/testers', buildTesterProfileDocument(profile));
}

/**
 * Provisions a C2HL-owned tester login and linked Manual QA profile.
 *
 * @param {object} profile - Tester identity fields entered by Test Admin.
 * @returns {Promise<object>} Provisioning response with profile and temporary password when created.
 */
async function provisionTester(profile) {
  return postJson(
    `/manual-qa/admin/${encodeURIComponent(MANUAL_QA_MASTER_TENANT_ID)}/testers/provision`,
    buildTesterProvisioningRequest(profile)
  );
}

/**
 * Saves a scenario catalog definition.
 *
 * @param {object} catalog - Catalog document.
 * @returns {Promise<object>} Persisted catalog.
 */
async function saveScenarioCatalog(catalog) {
  return postJson('/manual-qa/catalogs', buildScenarioCatalogDocument(catalog));
}

/**
 * Saves a Manual QA assignment for a tester.
 *
 * @param {object} assignment - Assignment document.
 * @returns {Promise<object>} Persisted assignment.
 */
async function saveAssignment(assignment) {
  return postJson('/manual-qa/assignments', buildAssignmentDocument(assignment));
}

/**
 * Saves screenshot or recording evidence metadata.
 *
 * @param {object} runState - Current tester run state.
 * @param {object} evidence - Evidence metadata.
 * @returns {Promise<object>} Persisted evidence document.
 */
async function saveEvidenceMetadata(runState, evidence) {
  return postJson('/manual-qa/evidence', buildEvidenceDocument(runState, evidence));
}

/**
 * Uploads screenshot or recording evidence bytes and persists server-owned metadata.
 *
 * @param {object} runState - Current tester run state.
 * @param {object} evidence - Evidence metadata.
 * @param {File} file - Browser file selected by the tester.
 * @returns {Promise<object>} Persisted evidence document.
 */
async function saveEvidenceFile(runState, evidence, file) {
  if (!file) {
    throw new Error('Evidence file is required');
  }

  const document = buildEvidenceDocument(runState, evidence);
  const formData = new FormData();
  formData.append('tenantId', document.tenantId);
  formData.append('runId', document.runId);
  formData.append('testerUserId', document.testerUserId);
  formData.append('bugReportId', document.bugReportId || '');
  formData.append('fileName', document.fileName || file.name);
  formData.append('contentType', document.contentType || file.type || 'application/octet-stream');
  formData.append('fileSizeBytes', String(document.sizeBytes || file.size || 0));
  formData.append('file', file, document.fileName || file.name);

  return postFormData('/manual-qa/evidence/upload', formData);
}

/**
 * Completes a Manual QA run and creates a pending hours review server-side.
 *
 * @param {object} runState - Local run state.
 * @param {object} plan - Scenario catalog plan.
 * @returns {Promise<object>} Completion response.
 */
async function completeRun(runState, plan) {
  return postJson(
    `/manual-qa/runs/${encodeURIComponent(getRunId(runState))}/complete`,
    buildCompletionReport(runState, plan)
  );
}

/**
 * Saves Test Admin payable-hours review.
 *
 * @param {string} runId - Stable run identifier.
 * @param {object} review - Hours review fields.
 * @returns {Promise<object>} Persisted hours review.
 */
async function saveHoursReview(runId, review) {
  return postJson(
    `/manual-qa/runs/${encodeURIComponent(runId)}/hours`,
    buildHoursReviewDocument(runId, review)
  );
}

/**
 * Lists Test Admin tester profiles.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Tester profile documents.
 */
async function listAdminTesters(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/testers`);
}

/**
 * Lists Test Admin assignments.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Assignment documents.
 */
async function listAdminAssignments(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/assignments`);
}

/**
 * Lists Test Admin runs.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Run documents.
 */
async function listAdminRuns(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/runs`);
}

/**
 * Lists Test Admin bug reports.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Bug report documents.
 */
async function listAdminBugs(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/bugs`);
}

/**
 * Requests server-side GitHub issue creation for an accepted Manual QA bug.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @param {string} bugId - Bug report document ID.
 * @returns {Promise<object>} Updated bug report document.
 */
async function createGithubIssue(tenantId = MANUAL_QA_MASTER_TENANT_ID, bugId) {
  if (!bugId) {
    throw new Error('bugId is required');
  }

  return postJson(
    `/manual-qa/admin/${encodeURIComponent(tenantId)}/bugs/${encodeURIComponent(bugId)}/github-issue`,
    {}
  );
}

/**
 * Runs advisory triage over a submitted bug and stores the verdict on it.
 *
 * The card the tester was on is sent from here because the catalogs are bundled with this
 * app rather than stored in Cosmos — without it the server cannot tell a real defect from a
 * tester misreading the instructions. Triage never files anything; it informs the person who
 * presses Create GitHub issue.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @param {string} bugId - Bug report document ID.
 * @param {object} cardContext - {title, purpose, howToTest[]} for the card the bug came from.
 * @returns {Promise<object>} Updated bug report document with triage attached.
 */
async function triageBug(tenantId = MANUAL_QA_MASTER_TENANT_ID, bugId, cardContext = null) {
  if (!bugId) {
    throw new Error('bugId is required');
  }

  return postJson(
    `/manual-qa/admin/${encodeURIComponent(tenantId)}/bugs/${encodeURIComponent(bugId)}/triage`,
    cardContext
      ? {
        title: cardContext.title || '',
        purpose: cardContext.purpose || '',
        howToTest: cardContext.howToTest || [],
      }
      : {}
  );
}

/**
 * Lists Test Admin completion reports.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Completion report documents.
 */
async function listAdminReports(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/reports`);
}

/**
 * Lists Test Admin payable-hours reviews.
 *
 * @param {string} tenantId - Manual QA tenant partition.
 * @returns {Promise<Array>} Hours review documents.
 */
async function listAdminHoursReviews(tenantId = MANUAL_QA_MASTER_TENANT_ID) {
  return getJson(`/manual-qa/admin/${encodeURIComponent(tenantId)}/hours`);
}

const manualQaApiService = {
  autosaveRun,
  submitBug,
  listAdminTesters,
  listAdminAssignments,
  listAdminRuns,
  listAdminBugs,
  createGithubIssue,
  triageBug,
  listAdminReports,
  listAdminHoursReviews,
  saveTesterProfile,
  provisionTester,
  saveScenarioCatalog,
  saveAssignment,
  saveEvidenceMetadata,
  saveEvidenceFile,
  completeRun,
  saveHoursReview,
  buildRunDocument,
  buildBugDocument,
  buildTesterProfileDocument,
  buildTesterProvisioningRequest,
  buildScenarioCatalogDocument,
  buildAssignmentDocument,
  buildEvidenceDocument,
  buildCompletionReport,
  buildHoursReviewDocument,
  getManualQaUserContext,
  buildManualQaAssignmentId,
  MANUAL_QA_MASTER_TENANT_ID,
};

export default manualQaApiService;
